<template>
    <BarChartCard
        :title="$t('shared.expenses')"
        :loading="detail ? statsPurchasePartitionMonthlyLoading : statsPurchaseMonthlyLoading"
        :data="detail ? statsPurchasePartitionMonthly : statsPurchaseMonthlyData"
        :activeBack="activeBack"
        :showYearNMinus1="true"
        :yearNMinus1="yearNMinus1"
        @onYearNMinus1Changed="
            (value) => {
                yearNMinus1 = value;
            }
        "
        @onSelectedTagsChanged="
            (value) => {
                selectedTags = value;
            }
        "
        :md6="false"
        :midHeight="true"
        @onClosingVue="
            () => {
                $emit('onClosingVue');
            }
        "
        :selectedTags="selectedTags"
        :showDetail="$vuetify.display.mobile ? false : true"
        :detail="detail"
        @onDetailChanged="
            (value) => {
                detail = value;
            }
        "
        :stacked="detail"
    >
    </BarChartCard>
</template>

<script lang="ts">
import { Apollo } from "@/decorators";
import gql from "graphql-tag";

import { chartColors } from "../../../../backend/src/common/chart/graphServices";

import { isNotSubjectToVAT } from "@/services/vat/vatHelpers.services";
import { Component, Prop, Vue, toNative } from "vue-facing-decorator";
import BarChartCard from "./BarChartCard.vue";

@Component({
    components: {
        BarChartCard,
    },
})
export class BarChartCardPurchase extends Vue {
    // @Prop({ default: "" }) readonly companyId: string
    @Prop({ default: "" }) readonly startDate: string;
    @Prop({ default: "" }) readonly endDate: string;
    @Prop({ default: "" }) readonly unitOfTime: string;
    @Prop({ default: true }) readonly activeBack: boolean;

    selectedTags = [];
    selectedPartitionTag = "";

    get company() {
        return this.$store.state.selected_company;
    }

    get companyId() {
        return this.company.id;
    }

    get amountType() {
        if (this.company && isNotSubjectToVAT(this.company.vatSystem)) return "grossAmountSigned";
        return "netAmountSigned";
    }

    yearNMinus1 = false;

    detail: boolean = false;

    @Apollo({
        query: gql`
            query (
                $companyId: ID!
                $selectedTags: [ID]
                $startDate: DateTime
                $endDate: DateTime
                $unitOfTime: String
                $amountType: String
                $yearNMinus1: Boolean
            ) {
                statsPurchaseMonthly(
                    data: {
                        companyId: $companyId
                        tags: $selectedTags
                        startDate: $startDate
                        endDate: $endDate
                        unitOfTime: $unitOfTime
                        amountType: $amountType
                        yearNMinus1: $yearNMinus1
                    }
                ) {
                    labels
                    dataset
                    datasets
                    tags
                }
            }
        `,
        skip() {
            return (
                this.detail ||
                !this.companyId ||
                !this.selectedTags ||
                !this.startDate ||
                !this.endDate
            );
        },
        variables() {
            return {
                companyId: this.companyId,
                selectedTags: this.selectedTags,
                startDate: this.startDate,
                endDate: this.endDate,
                unitOfTime: this.unitOfTime,
                amountType: this.amountType,
                yearNMinus1: this.yearNMinus1,
            };
        },
        debounce: 500,
        specificResult({ data, loading, networkStatus }: any) {
            this.statsPurchaseMonthlyLoading = loading;
            if (!loading) {
                if (
                    data &&
                    data.statsPurchaseMonthly &&
                    ((data.statsPurchaseMonthly.dataset &&
                        data.statsPurchaseMonthly.dataset.length) ||
                        (data.statsPurchaseMonthly.datasets &&
                            data.statsPurchaseMonthly.datasets.length))
                ) {
                    if (data.statsPurchaseMonthly.dataset) {
                        data.statsPurchaseMonthly.dataset = data.statsPurchaseMonthly.dataset.map(
                            (v) => -v,
                        );
                    }
                    if (data.statsPurchaseMonthly.datasets?.length) {
                        for (const dataIndex in data.statsPurchaseMonthly.datasets) {
                            data.statsPurchaseMonthly.datasets[dataIndex].data =
                                data.statsPurchaseMonthly.datasets[dataIndex].data.map((v) => -v);
                        }
                    }
                    this.statsPurchaseMonthlyData = data.statsPurchaseMonthly;
                } else {
                    this.statsPurchaseMonthlyData = null;
                }
            }
            return null;
        },
    })
    statsPurchaseMonthlyData: any = null;

    statsPurchaseMonthlyLoading: boolean = false;

    //
    // Purchase Partition Monthly
    //
    @Apollo({
        query: gql`
            query (
                $companyId: ID!
                $partitionTagId: ID
                $startDate: DateTime
                $endDate: DateTime
                $unitOfTime: String
                $amountType: String
            ) {
                statsPurchasePartitionMonthly(
                    data: {
                        companyId: $companyId
                        partitionTagId: $partitionTagId
                        startDate: $startDate
                        endDate: $endDate
                        unitOfTime: $unitOfTime
                        amountType: $amountType
                    }
                ) {
                    labels
                    datasets
                }
            }
        `,
        skip() {
            return !this.detail || !this.companyId || !this.startDate || !this.endDate;
        },
        variables() {
            return {
                companyId: this.companyId,
                partitionTagId: this.selectedPartitionTag ? this.selectedPartitionTag : "",
                startDate: this.startDate,
                endDate: this.endDate,
                unitOfTime: this.unitOfTime,
                amountType: this.amountType,
            };
        },
        debounce: 500,
        specificResult({ data, loading, networkStatus }: any) {
            this.statsPurchasePartitionMonthlyLoading = loading;
            if (!loading) {
                if (
                    data &&
                    data.statsPurchasePartitionMonthly &&
                    data.statsPurchasePartitionMonthly.labels &&
                    data.statsPurchasePartitionMonthly.labels.length
                ) {
                    // hack to avoid cpu loop
                    if (
                        data.statsPurchasePartitionMonthly.datasets &&
                        data.statsPurchasePartitionMonthly.datasets.length &&
                        data.statsPurchasePartitionMonthly.datasets[0].backgroundColor
                    ) {
                        debugger;
                        return;
                    }
                    const datasets = JSON.parse(
                        JSON.stringify(data.statsPurchasePartitionMonthly.datasets),
                    );
                    chartColors("bar", datasets, data.currentPalette);
                    this.statsPurchasePartitionMonthly = {
                        labels: data.statsPurchasePartitionMonthly.labels,
                        datasets: datasets,
                    };
                } else {
                    this.statsPurchasePartitionMonthly = null;
                }
            }
        },
    })
    statsPurchasePartitionMonthly: any = null;

    statsPurchasePartitionMonthlyLoading: boolean = false;
}

export default toNative(BarChartCardPurchase);
</script>
